import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import ScrollToTop from '@/components/base/ScrollToTop';
import { getComicById, getComicsByCategory } from '@/mocks/comics';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const comic = getComicById(slug || '');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  if (!comic) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-20 pb-20 text-center">
          <h1 className="text-2xl font-heading text-foreground-950 mb-4">漫画未找到</h1>
          <Link to="/" className="text-primary-500 hover:text-primary-600 cursor-pointer">返回首页</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const relatedComics = getComicsByCategory(comic.categorySlug)
    .filter((c) => c.id !== comic.id)
    .slice(0, 4);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - 51漫画免费下载`,
    description: comic.description,
    author: { '@type': 'Person', name: comic.author },
    datePublished: comic.publishDate,
    dateModified: comic.updateDate,
    url: `https://51manhua.com/article/${comic.id}`,
    publisher: {
      '@type': 'Organization',
      name: '51漫画',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://51manhua.com/article/${comic.id}`,
    },
  };

  return (
    <div className="min-h-screen bg-background-50">
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <Navbar />

      <main className="pt-16">
        <div className="relative w-full h-52 md:h-72 overflow-hidden">
          <img
            src={comic.banner}
            alt={`${comic.title} - 51漫画免费下载`}
            title={`51漫画 - ${comic.title}`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/50 to-transparent"></div>
        </div>

        <div className="max-w-4xl mx-auto px-4 md:px-6 -mt-24 relative z-10">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: comic.category, to: `/category/${comic.categorySlug}` },
              { label: comic.title },
            ]}
          />

          <div className="flex flex-col sm:flex-row gap-6 mb-8">
            <div className="flex-shrink-0 w-36 h-48 md:w-40 md:h-56 rounded-lg overflow-hidden bg-background-200">
              <img
                src={comic.cover}
                alt={`${comic.title}封面 - 51漫画免费下载`}
                title={`51漫画 - ${comic.title}免费下载`}
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="flex flex-col justify-center">
              <h1 className="text-xl md:text-2xl font-heading text-foreground-950 mb-2">
                <strong>{comic.title}</strong>
              </h1>
              <p className="text-sm text-foreground-500 mb-3">{comic.author} 著</p>
              <div className="flex flex-wrap items-center gap-3 mb-3">
                <Link
                  to={`/category/${comic.categorySlug}`}
                  className="inline-flex items-center px-3 py-1 bg-secondary-100 text-secondary-700 rounded-full text-xs font-medium hover:bg-secondary-200 transition-colors duration-200 cursor-pointer whitespace-nowrap"
                >
                  {comic.category}
                </Link>
                <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                  comic.status === '连载中'
                    ? 'bg-primary-100 text-primary-700'
                    : 'bg-secondary-200 text-secondary-700'
                }`}>
                  {comic.status}
                </span>
                <div className="flex items-center gap-1 text-sm">
                  <i className="ri-star-fill text-accent-500"></i>
                  <span className="font-semibold text-foreground-800">{comic.rating}</span>
                </div>
              </div>
              <div className="flex flex-wrap gap-3 text-xs text-foreground-500">
                <span><i className="ri-book-open-line mr-1"></i>共 {comic.chapters} 话</span>
                <span><i className="ri-eye-line mr-1"></i>{comic.views} 次阅读</span>
                <span><i className="ri-calendar-line mr-1"></i>更新于 {comic.updateDate}</span>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-3">
                {comic.tags.map((tag) => (
                  <span key={tag} className="inline-block px-2 py-0.5 bg-background-100 text-foreground-600 rounded text-xs whitespace-nowrap">
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <section className="mb-10">
            <h2 className="text-lg font-semibold text-foreground-900 mb-3">作品简介</h2>
            <p className="text-sm text-foreground-600 leading-relaxed">{comic.fullDescription}</p>
          </section>

          <section className="mb-10">
            <h2 className="text-lg font-semibold text-foreground-900 mb-4">精彩预览</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {comic.screenshots.map((src, i) => (
                <div key={i} className="rounded-lg overflow-hidden bg-background-200 aspect-video">
                  <img
                    src={src}
                    alt={`${comic.title} 精彩截图 ${i + 1}`}
                    title={`51漫画 - ${comic.title}预览`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              ))}
            </div>
          </section>

          <section className="mb-14">
            <h2 className="text-lg font-semibold text-foreground-900 mb-4">
              下载<strong>{comic.title}</strong>
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {comic.downloadLinks.map((link, i) => (
                <a
                  key={i}
                  href={link.url}
                  className="flex items-center justify-center gap-2 px-5 py-3.5 bg-primary-500 text-background-50 rounded-lg text-sm font-semibold hover:bg-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
                >
                  <i className={`${link.icon} text-lg`}></i>
                  {link.platform}
                </a>
              ))}
            </div>
            <p className="text-xs text-foreground-400 mt-3">
              通过<strong>51漫画APP下载</strong>即可<strong>离线阅读</strong>完整版{comic.title}
            </p>
          </section>

          {relatedComics.length > 0 && (
            <section className="pb-14">
              <h2 className="text-lg font-semibold text-foreground-900 mb-4">
                相关<strong>漫画推荐</strong>
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-product-shop="">
                {relatedComics.map((rc) => (
                  <ComicCard key={rc.id} comic={rc} />
                ))}
              </div>
            </section>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}